<script setup>
import { batchViewPath } from '@/helpers/batch'
import { useUserStore } from '@/stores/UserStore'
import { ref, computed, watch } from 'vue'
import {
  TransitionRoot, TransitionChild, Dialog, DialogPanel,
  Combobox, ComboboxInput, ComboboxOptions, ComboboxOption,
  Listbox, ListboxButton, ListboxOptions, ListboxOption
} from '@headlessui/vue'
import { useRouter } from 'vue-router'
import { useGlobalStore } from '@/stores/GlobalStore'
import { useSearchStore } from '@/stores/SearchStore'
import { useCardStore } from '@/stores/CardStore'
import { searchFilters } from '@/constants'
import { isSearchInventoryEnabled } from '@/helpers/featureFlags'

import { useBatchStore } from '@/stores/BatchStore'
import debounce from 'lodash/debounce'
import SearchBatchesItem from './SearchBatchesItem.vue'
import SearchDatabaseItem from './SearchDatabaseItem.vue'
import SearchInventoryItem from './SearchInventoryItem.vue'
import { groupInventoryHits } from '@/helpers/globalSearch'
import SearchEmptyState from './SearchEmptyState.vue'
import SearchFilterPills from './SearchFilterPills.vue'
import SectionHeader from './SectionHeader.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import { getParamsBasedOnLockedSets, isBatchLockedForSet } from '@/helpers/searchDatabaseMatchField'
import SetLockSearchIndicator from '@/components/SetLockIndicator/SetLockSearchIndicator.vue'

const router = useRouter()

const globalStore = useGlobalStore()
const searchStore = useSearchStore()
const batchStore = useBatchStore()
const userStore = useUserStore()
const cardStore = useCardStore()
const selected = ref('')
const query = ref('')
const inputRef = ref(null)
const selectedCard = ref(null)

const openedSearch = computed(() => searchStore.cdp_search.open)
const triggerOpenSearchResult = computed(() => searchStore.triggerOpenSearchResult)

const searchInputDefaultValues = computed(() => {
  if (searchStore.cdp_search.state === 'database') {
    return {
      search_in: 'Card Database',
      placeholder: 'Search card database',
      results_title: 'Database'
    }
  } else if (searchStore.cdp_search.state === 'batches') {
    return {
      search_in: 'Batches',
      placeholder: 'Search all batches',
      results_title: 'Batches'
    }
  } else if (searchStore.cdp_search.state === 'inventory') {
    return {
      search_in: 'Inventory',
      placeholder: 'Search all inventory',
      results_title: 'Inventory'
    }
  }
})

/** How many of the user's own cards the response carried. */
const inventoryHits = computed(() => searchStore.inventoryData.items.length)

const noResultsFound = computed(() => {
  if (searchStore.selectedFilter === 'Card Database' && query.value && !searchStore.matchesData.matches.length) {
    return true
  } else if ((searchStore.selectedFilter === 'Card Database' && !query.value) || (searchStore.selectedFilter === 'Card Database' && query.value && searchStore.matchesData.matches.length)) {
    return false
  }

  if (searchStore.selectedFilter === 'Batches' && query.value && !searchStore.batchesSearched.length) {
    return true
  } else if ((searchStore.selectedFilter === 'Batches' && !query.value) || (searchStore.selectedFilter === 'Batches' && query.value && searchStore.batchesSearched.length)) {
    return false
  }

  // Same shape as the two filters above, for the Inventory filter.
  if (searchStore.selectedFilter === 'Inventory' && query.value && !inventoryHits.value) {
    return true
  } else if (searchStore.selectedFilter === 'Inventory') {
    return false
  }

  // Unfiltered: inventory hits alone are a result. Without this a query that
  // only matches the user's OWN cards would report "nothing found" while the
  // Inventory section sat right below the message.
  if (
    !searchStore.matchesData.matches?.length
    && !searchStore.batchesSearched?.length
    && !inventoryHits.value
    && searchStore.searchState === 'result'
  ) {
    return true
  } else {
    return false 
  }
})

const dialogHasOptions = computed(() => {
  if (noResultsFound.value || searchStore.searchState === 'start' || !searchStore.selectedFilter || (searchStore.searchState && query.value) || (searchStore.selectedFilter === 'Card Database') || (searchStore.selectedFilter === 'Inventory') || (searchStore.selectedFilter === 'Batches' && !query.value)) {
    return true
  } else {
    return false
  }
})

/**
 * The user's own cards among the results (CDP-2508), one row per CARD.
 *
 * Grouped because the API returns each owned copy separately — the same card in
 * three batches is three hits — and the row wants to say "in 3 batches" rather
 * than repeat itself three times.
 *
 * Empty for everyone outside the pilot; the store drops the block.
 */
const filters = computed(() => searchFilters(isSearchInventoryEnabled()))

const inventoryGroups = computed(() => groupInventoryHits(searchStore.inventoryData.items))

const handleLoadMore = () => {
  const searchOptions = { query: query.value, page: searchStore.matchesData.page + 1 }

  if (batchStore.batchKind) {
    searchOptions.kind = batchStore.batchKind
  }  

  const params = getParamsBasedOnLockedSets(batchStore.selectedBatch?.locked_sets)

  if (params.baseSetIds) {
    searchOptions['baseSetIds'] = params.baseSetIds
  }

  if (params.setIds) {
    searchOptions['setIds'] = params.setIds
  }

  searchStore.searchDatabaseMatches(searchOptions, true) // second argument is true to skip loader update because we are loading more matches
}

/**
 * The inventory block paginates independently of the catalog one — same
 * request, its own `page` / `totalPages` — so it needs its own "Load more".
 * `setInventoryData` appends when `page > 1`.
 */
const handleLoadMoreInventory = () => {
  searchStore.searchDatabaseMatches(
    { query: query.value, page: searchStore.inventoryData.page + 1 },
    true, // skip the loader: the results below stay put while more arrive
  )
}

const hasMoreInventory = computed(() =>
  searchStore.inventoryData.page < searchStore.inventoryData.totalPages)

const onSearchCDP = debounce(event => {
  query.value = event.target.value

  let params = {}

  if (batchStore.selectedBatch?.id) {
    params = getParamsBasedOnLockedSets(batchStore.selectedBatch?.locked_sets)
  }

  if (searchStore.selectedFilter === 'Card Database') {

    if (batchStore.selectedBatch?.id) {
      searchStore.searchDatabaseMatches({ query: query.value, kind: batchStore.selectedBatch.type, baseSetIds: params.baseSetIds || '', setIds: params.setIds || '' })
    } else {
      searchStore.searchDatabaseMatches({ query: query.value, baseSetIds: params.baseSetIds || '' })
    }
    
    return
  }

  if (searchStore.selectedFilter === 'Batches') {
    if (selectedCard.value) {
      searchStore.searchUserBatches({ query: query.value, kind: selectedCard.value.game ? 'tcg' : selectedCard.value.sport ? 'sports' : '' })
    } else {
      searchStore.searchUserBatches({ query: query.value })
    }
    return
  }

  // The inventory block rides along with `/search`, so the filter still has to
  // FIRE that request — without this branch, typing under the Inventory filter
  // fell through to `searchState = 'suggestion'`, no request was made, and the
  // results never appeared (which is what made Enter look like a no-op).
  // Deliberately unfiltered by locked sets: those constrain which CATALOG cards
  // may be added to a batch, and say nothing about the cards you already own.
  if (searchStore.selectedFilter === 'Inventory') {
    searchStore.searchDatabaseMatches({ query: query.value })
    searchStore.searchState = 'result'
    return
  }

  if (!searchStore.selectedFilter) {
    if (batchStore.selectedBatch?.id) {
      batchStore.batchKind = batchStore.selectedBatch.type
      searchStore.searchDatabaseMatches({ query: query.value, kind: batchStore.selectedBatch.type, baseSetIds: params.baseSetIds || '', setIds: params.setIds || '' })
      searchStore.searchUserBatches({ query: query.value, kind: batchStore.selectedBatch.type })
    } else {
      searchStore.searchDatabaseMatches({ query: query.value })
      searchStore.searchUserBatches({ query: query.value })
    }

    searchStore.searchState = 'result'
    return
  }

  if (query.value) {
    searchStore.searchState = 'suggestion'
  } else {
    if (!searchStore.selectedFilter) {
      searchStore.searchState = 'start'
    }
  }
}, 400)

const onRemoveSearch = () => {
  if (!query.value && searchStore.selectedFilter) {
    const searchOptions = {}

    if (batchStore.batchKind) {
      searchOptions.kind = batchStore.batchKind
    }

    searchStore.selectedFilter = ''
    searchStore.searchState = 'result'
    searchStore.searchDatabaseMatches(searchOptions)
    searchStore.searchUserBatches(searchOptions)
  }
}

const selectSearchFilter = option => {
  searchStore.selectedFilter = option

  const batchKind = batchStore.selectedBatch ? batchStore.selectedBatch.type : selectedCard.value?.game ? 'tcg' : selectedCard.value?.sport ? 'sports' : ''

  if (option === 'Card Database') {
    searchStore.searchState = 'result'
    query.value = ''
    searchStore.cdp_search.state = 'database'

    inputRef.value.el.focus()
  } else if (option === 'Inventory') {
    // No separate request: the inventory block rides along with /search, so the
    // filter only narrows what's rendered.
    searchStore.searchState = 'result'
    query.value = ''
    searchStore.cdp_search.state = 'inventory'

    inputRef.value.el.focus()
  } else if (option === 'Batches') {
    searchStore.searchState = 'result'
    query.value = ''
    searchStore.cdp_search.state = 'batches'
    
    inputRef.value.el.focus()

    if (batchKind) {
      searchStore.searchUserBatches({ kind: batchKind }) 
    } else {
      searchStore.searchUserBatches()
    }
  } else {
    if (batchKind) {
      searchStore.searchUserBatches({ kind: batchKind }) 
    } else {
      searchStore.searchUserBatches() 
    }

    searchStore.searchState = 'suggestion'
  }
}

/**
 * Is a real batch open right now?
 *
 * New design only checks for an `id`: Inventory can leave a placeholder
 * `selectedBatch` behind (`{ type }`, no id — see the bulk-edit type probe), and
 * a truthiness test would read that as "a batch is open" and quietly add the
 * card to nowhere. Classic keeps the original test so its flows are untouched.
 */
const hasOpenBatch = () => (userStore.isNewDesign
  ? !!batchStore.selectedBatch?.id
  : !!batchStore.selectedBatch)

/**
 * Where to land after a card is added to a batch.
 *
 * New design: that batch inside Inventory with the Inspector OPEN on the card
 * just added — adding it is the start of filling in its details, not the end.
 * Falls back to the inspector's own default card when the API didn't hand back
 * an id. Classic design is unchanged: the batch grid.
 */
const afterAddPath = (batchId, addedCard) => {
  if (!userStore.isNewDesign) return batchViewPath(batchId)

  const cardId = addedCard?.id || addedCard?.card?.id

  return batchViewPath(batchId, cardId ? { cardId } : { inspector: true })
}

const showResults = async (value, type = '') => {
  if (selectedCard.value && isBatchLockedForSet(batchStore.selectedBatch, value)) return 

  if (searchStore.selectedFilter && !hasOpenBatch() && type === 'card') {
    selected.value = value
    searchStore.selectedFilter = 'Batches'
    inputRef.value.el.value = ''
    searchStore.cdp_search.locationOfSearchCall = 'add-button'
    selectSearchFilter('Batches')
    return
  }
  
  if (!searchStore.selectedFilter && batchStore.selectedBatch && !Object.keys(value).includes('number')) {
    batchStore.selectedBatch = { ...value }
    batchStore.selectedBatchId = value.id 
    cardStore.fetchSelectedCards()

    closeDialog()
    return
  }

  if (searchStore.cdp_search.locationOfSearchCall === 'add-match') {
    cardStore.updateMatchedCard(value)
    closeDialog()
    return
  }

  if (searchStore.cdp_search.locationOfSearchCall === 'add-card-button') {
    cardStore.importMatchedCard(value)
    closeDialog()
    return
  }

  if (searchStore.cdp_search.locationOfSearchCall === 'add-button' && searchStore.selectedFilter === 'Batches') {
    if (isBatchLockedForSet(batchStore.selectedBatch, value)) return 

    if (selected.value) {
      batchStore.selectedBatchId = value.id

      // Awaited so the redirect can open the Inspector on the card the API
      // just created.
      const added = await cardStore.importMatchedCard(selected.value)
      selected.value = null

      router.push(afterAddPath(value.id, added))
      closeDialog()
      return
    } else {
      router.push(`/batches/${value.id}/add/types`)
    }
    
    closeDialog()
    return
  } else if (searchStore.selectedFilter === 'Batches' && selectedCard.value) {
    if (isBatchLockedForSet(batchStore.selectedBatch, value)) return 
    
    batchStore.selectedBatchId = value.id
    const added = await cardStore.importMatchedCard(selectedCard.value)
    router.push(afterAddPath(value.id, added))
    selectedCard.value = null
    closeDialog()
    return
  } else if (searchStore.selectedFilter === 'Batches') {
    router.push(batchViewPath(value.id))
    batchStore.updateSelectedBatch({ data: value })
    cardStore.fetchSelectedCards()
    closeDialog()
    return
  }

  // A batch is open (including batch-in-inventory) — "Add Card" adds to THAT
  // batch, then opens the new card in the Inspector.
  if (Object.keys(value).includes('number') && batchStore.selectedBatch?.id) {
    const added = await cardStore.importMatchedCard(value)
    router.push(afterAddPath(batchStore.selectedBatch.id, added))
    closeDialog()
    return
  }

  if (!selectedCard.value && !searchStore.selectedFilter && 'title_template_id' in value) {
    router.push(batchViewPath(value.id))
    closeDialog()
    return
  }
 
  if (!window.getSelection().toString() && value) {
    query.value = ''
    inputRef.value.el.value = ''
    inputRef.value.el.focus()
    selectedCard.value = value
    selectSearchFilter('Batches')
    localStorage.setItem(`selectedCard`, JSON.stringify(value))
    
    searchStore.cdp_search = { ...searchStore.cdp_search, locationOfSearchCall: '' }
  }
}

const openDialog = () => {
  searchStore.cdp_search.open = true

  if (!searchStore.selectedFilter) {
    searchStore.searchState = 'start'
  }
}

const closeDialog = () => {
  batchStore.batchKind = ''
  searchStore.cdp_search.open = false
  searchStore.triggerOpenSearchResult = false
  setTimeout(() => {
    searchStore.selectedFilter = ''
    query.value = ''
    selected.value = ''
    selectedCard.value = null
    searchStore.searchState = 'start'
  }, 300)
}

watch(openedSearch, () => {
  setTimeout(() => {
    openedSearch && inputRef.value && inputRef.value.el.focus()
  }, 0)

  if (!openedSearch.value) {
    searchStore.matchesData = { matches: [] }
  }
})

watch(triggerOpenSearchResult, () => {
  triggerOpenSearchResult.value && openDialog()
})

watch(() => searchStore.selectedFilter, () => {
  if (searchStore.selectedFilter === 'Card Database') {
    searchStore.cdp_search.state = 'database'
  } else {
    searchStore.cdp_search.state = 'batches'
  }
});
</script>


<template>
  <div class="block relative z-10 w-[432px] text-sm">
    <div
      class="
        relative overflow-hidden rounded-full items-center justify-between gap-x-3 bg-white border py-1.5
        px-2.5 h-9 text-gray-600 cursor-pointer hidden md:flex
      "
      @click="openDialog"
    >
      <Icon icon="heroicons:magnifying-glass" class="text-lg" />
      <div class="pointer-events-none flex items-center gap-x-2">
        <Icon
          icon="mdi:apple-keyboard-command"
          class="w-6 h-6 rounded p-1 bg-gray-100"
          v-if="globalStore.userAgent === 'cmd'"
        />
        <Icon icon="vaadin:ctrl-a" class="w-6 h-6 rounded p-1 bg-gray-100" v-else />
        <span>+</span>
        <span class="bg-gray-100 w-6 h-6 rounded flex items-center justify-center"> K </span>
      </div>
    </div>
  </div>

  <TransitionRoot as="template" :show="searchStore.cdp_search.open">
    <Dialog as="div" class="relative z-[9999] text-xs" @close="closeDialog">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black bg-opacity-[0.38] transition-opacity backdrop-blur-sm" />
      </TransitionChild>
  
      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div class="flex min-h-full w-full">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 lg:translate-y-0 lg:scale-95"
            enter-to="opacity-100 translate-y-0 lg:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 lg:scale-100"
            leave-to="opacity-0 translate-y-4 lg:translate-y-0 lg:scale-95"
          >
            <DialogPanel class="relative mx-auto lg:py-2 w-full lg:max-w-[50rem] h-fit">
              <Combobox
                :modelValue="selected"
                @update:modelValue="showResults"
              >
                <div class="header-wrapper flex items-center max-lg:gap-x-2 max-lg:bg-tint-1 w-full max-lg:px-4 max-lg:h-12">
                  <header
                    class="flex-1 flex items-center gap-x-2 py-2.5 px-4 bg-white relative z-10 max-lg:rounded-full"
                    :class="dialogHasOptions ? 'lg:rounded-t-xl' : 'lg:rounded-3xl'"
                  >
                    <Icon icon="heroicons:magnifying-glass" class="text-lg flex-none" />
                    <Listbox v-model="searchStore.selectedFilter" class="relative flex-none" as="div" v-if="searchStore.selectedFilter">
                      <ListboxButton class="rounded-sm bg-blue-100 py-0.5 px-1">in: {{ searchStore.selectedFilter }}</ListboxButton>
                      <ListboxOptions
                        class="absolute top-full mt-px left-0 w-28 child:p-1.5 child:cursor-pointer"
                      >
                        <ListboxOption
                          v-for="(option, index) in filters"
                          :value="option"
                          :key="index"
                          v-slot="{ selected }"
                          as="template"
                        >
                          <li v-text="option" :class="selected ? 'bg-blue-200 font-semibold text-tint-1' : 'bg-blue-100'" />
                        </ListboxOption>
                      </ListboxOptions>
                    </Listbox>
                    <ComboboxInput
                      class="bg-transparent focus:ring-0 border-0 p-0 text-sm flex-1"
                      @change.stop.prevent="onSearchCDP"
                      @keydown.delete="onRemoveSearch"
                      :placeholder="query || (searchInputDefaultValues && searchInputDefaultValues ? searchInputDefaultValues.placeholder : 'Search card database and batches ...')"
                      ref="inputRef"
                    />
                  </header>

                  <button class="lg:hidden text-3xl text-white" @click="closeDialog">
                    <Icon icon="heroicons:x-mark-16-solid" />
                  </button>
                </div>

                <div class="bg-white w-full lg:w-[800px] relative px-4 py-2" v-if="(batchStore.selectedBatch?.locked_sets?.length || batchStore.selectedBatch?.lock_id) && searchStore.selectedFilter === 'Card Database'">
                  <SetLockSearchIndicator />
                </div>

                <ComboboxOptions
                  class="
                    absolute top-12 left-0 border-t max-h-[calc(100vh-11.5rem)] lg:max-h-[calc(100vh-6rem)] w-full overflow-y-auto rounded-b-xl
                    bg-white focus:outline-none
                  "
                  v-if="dialogHasOptions"
                  static
                >
                  <div v-if="searchStore.loadingMatches || searchStore.loadingBatches" class="h-[294px] flex justify-center">
                    <LoadingSpinner :width="'112px'" :height="'112px'" />
                  </div>

                  <template v-else>
                    <SearchEmptyState v-if="noResultsFound" />

                    <template v-else-if="searchStore.searchState === 'start'">
                      <SearchFilterPills v-if="!searchStore.selectedFilter" @select="selectSearchFilter" />
                    </template>

                    <template v-else-if="searchStore.searchState === 'result'">

                      <SearchFilterPills v-if="!searchStore.selectedFilter" @select="selectSearchFilter" />

                      <template v-if="searchStore.selectedFilter === 'Batches' && batchStore.selectedBatch && searchStore.cdp_search.locationOfSearchCall === 'add-button'">
                        <section>
                          <SectionHeader :placeholder="'Add to this batch'" />
                          
                          <ComboboxOption
                            :value="batchStore.selectedBatch"
                            as="div"
                            v-slot="{ active }"
                          >
                            <SearchBatchesItem :active="active" :item="batchStore.selectedBatch" />
                          </ComboboxOption> 
                        </section>
                      </template>

                      <template v-if="searchStore.selectedFilter === 'Batches' && searchStore.recentlyModifiedBatches.length">
                        <section>
                          <SectionHeader :placeholder="'Recently modified'" />
                         
                          <ComboboxOption
                            v-for="(option, index) in searchStore.recentlyModifiedBatches"
                            :key="index"
                            :value="option"
                            v-slot="{ active }"
                          >
                            <SearchBatchesItem :active="active" :item="option" :selectedCard="selectedCard" />
                            
                          </ComboboxOption>  
                        </section>
                      </template>
  
                      <template v-if="searchStore.batchesSearched.length">
                        <section v-if="!searchStore.selectedFilter || searchStore.selectedFilter === 'Batches'">
                          <SectionHeader :placeholder="'Batches'" :items="searchStore.batchesSearched" :filter="'Batches'" :buttonName="'All batch results'" />
  
                          <ComboboxOption
                            v-for="(option, index) in !searchStore.selectedFilter ? searchStore.batchesSearched.slice(0, 3) : searchStore.batchesSearched"
                            :key="index"
                            :value="option"
                            v-slot="{ active }"
                          >
                            <SearchBatchesItem :active="active" :item="option" :selectedCard="selectedCard" />
                          </ComboboxOption>  
                        </section>
                      </template>
  
                      <!-- The user's OWN cards. Its own section, not a badge on
                           the database rows: inventory hits carry no match id, so
                           there is nothing to attach them to. -->
                      <template v-if="inventoryGroups.length">
                        <section v-if="!searchStore.selectedFilter || searchStore.selectedFilter === 'Inventory'">
                          <SectionHeader
                            :placeholder="'Inventory'"
                            :items="inventoryGroups"
                            :filter="'Inventory'"
                            :buttonName="'All inventory results'"
                          />

                          <ComboboxOption
                            v-for="group in !searchStore.selectedFilter ? inventoryGroups.slice(0, 3) : inventoryGroups"
                            :key="group.key"
                            :value="group.card"
                            v-slot="{ active }"
                          >
                            <SearchInventoryItem :active="active" :group="group" />
                          </ComboboxOption>
                          <div
                            v-if="hasMoreInventory && searchStore.selectedFilter === 'Inventory'"
                            class="flex flex-col justify-center items-center gap-y-3 py-4"
                          >
                            <button class="alternative-button text-tint-1 text-sm" @click.stop.prevent="handleLoadMoreInventory">
                              <template v-if="searchStore.loadingMoreMatches">
                                <LoadingSpinner :width="'16px'" :height="'16px'" />
                              </template>
                              <template v-else>
                                Load more
                              </template>
                            </button>
                          </div>
                        </section>
                      </template>

                      <template v-if="searchStore.matchesData.matches.length">
                        <section v-if="!searchStore.selectedFilter || searchStore.selectedFilter === 'Card Database'">
                          <SectionHeader :placeholder="'Database'" :items="searchStore.matchesData.matches" :filter="'Card Database'" :buttonName="'All database results'" />
                          
                          <ComboboxOption
                            v-for="(option, index) in !searchStore.selectedFilter ? searchStore.matchesData.matches.slice(0, 3) : searchStore.matchesData.matches"
                            :key="index"
                            :value="option"
                            v-slot="{ active }"
                          >
                            <SearchDatabaseItem :active="active" :item="option" />
                          </ComboboxOption>
                          <div v-if="(searchStore.matchesData.totalHits > searchStore.matchesData.matches.length) && searchStore.selectedFilter === 'Card Database'" class="flex flex-col justify-center items-center gap-y-3 py-4">
                            <button class="alternative-button text-tint-1 text-sm" @click.stop.prevent="handleLoadMore">
                              <template v-if="searchStore.loadingMoreMatches">
                                <LoadingSpinner :width="'16px'" :height="'16px'" />
                              </template>
                              <template v-else>
                                Load more
                              </template>
                            </button>
                          </div>
                        </section>
                      </template>
                    </template>
                  </template>
                </ComboboxOptions>
              </Combobox>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>